import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import type { RosDashboardFilters, RosKpi } from './types'
import { useCanOpenQueue } from '../../procurement/queueAccess'
import { queueLink } from './useRosDashboard'

// KPI-плитки дашборда РОЗ — стиль «Моя работа · Закупщик» (ProcurementKpiCards):
// rounded-2xl, пастельный фон, крупное жирное число. Плитки кликабельны и ведут
// в очередь квот с соответствующим серверным фильтром. У ADMIN очередь закрыта
// (18.09) — плитки очереди у него без ссылки (решение Сергея 25.09).

type Tone = 'blue' | 'neutral' | 'red' | 'amber' | 'emerald' | 'violet'

const BG: Record<Tone, string> = {
  blue: 'bg-blue-50/60 border-blue-100',
  neutral: 'bg-gray-50 border-gray-200',
  red: 'bg-red-50/60 border-red-100',
  amber: 'bg-amber-50/60 border-amber-100',
  emerald: 'bg-emerald-50/60 border-emerald-100',
  violet: 'bg-violet-50/60 border-violet-100',
}

function Card({
  label,
  value,
  tone = 'neutral',
  hint,
  to,
  onClick,
}: {
  label: string
  value: string | number
  tone?: Tone
  hint?: ReactNode
  to?: string
  onClick?: () => void
}) {
  const inner = (
    <div className={`rounded-2xl border ${BG[tone]} p-4 h-full flex flex-col justify-between`}>
      <div className="text-sm text-gray-600">{label}</div>
      <div className="text-3xl font-bold text-gray-900 leading-tight mt-2">{value}</div>
      {hint && <div className="mt-1">{hint}</div>}
    </div>
  )
  if (onClick)
    return (
      <button
        onClick={onClick}
        className="block w-full h-full text-left transition-transform hover:-translate-y-0.5"
      >
        {inner}
      </button>
    )
  if (!to) return inner
  return (
    <Link to={to} className="block h-full transition-transform hover:-translate-y-0.5">
      {inner}
    </Link>
  )
}

export function RosKpiCards({
  kpi,
  filters,
  onOpenDrill,
}: {
  kpi: RosKpi
  filters: RosDashboardFilters
  onOpenDrill: (kind: 'quoted') => void
}) {
  const canQueue = useCanOpenQueue()
  const toQueue = (params: Record<string, string>) =>
    canQueue ? queueLink(params, filters) : undefined
  const split = (s: { tekuchka: number; project: number }) => (
    <span className="text-xs text-gray-500">
      текучка {s.tekuchka} · проекты {s.project}
    </span>
  )
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      {/* Только необработанные (новые + в работе) — та же цифра, что суммарно
          в секциях «Новые»+«В работе» очереди, куда ведёт клик. */}
      <Card
        label="Позиций в очереди"
        value={kpi.queueItems.total}
        tone="blue"
        hint={split(kpi.queueItems)}
        to={toQueue({})}
      />
      {/* Только активные заявки (без Счёта/Архива); клик — в раздел «Заявки»,
          активные там в верхней секции. */}
      <Card
        label="Заявок в работе"
        value={kpi.requestsInWork.total}
        tone="neutral"
        hint={split(kpi.requestsInWork)}
        to="/requests"
      />
      <Card
        label="Новых позиций"
        value={kpi.newItems}
        tone="violet"
        hint={<span className="text-xs text-violet-700">никем не тронуты</span>}
        to={toQueue({ new: '1' })}
      />
      <Card
        label="Просрочено"
        value={kpi.overdue}
        tone="red"
        hint={<span className="text-xs text-red-700">дедлайн истёк · новые и в работе</span>}
        to={toQueue({ overdue: '1', activeOnly: '1' })}
      />
      <Card
        label="Проквотировано"
        value={kpi.quoted}
        tone="emerald"
        hint={<span className="text-xs text-emerald-700">за период · список</span>}
        onClick={() => onOpenDrill('quoted')}
      />
      <Card
        label="Без предложения"
        value={kpi.noOffer}
        tone="amber"
        hint={<span className="text-xs text-amber-700">нет ответа / отказ / не найдено</span>}
        to={toQueue({ noOffer: '1' })}
      />
    </div>
  )
}
